import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import ExitPopup941 from '@/components/ui/ExitPopup941';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941MailAddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Where to mail IRS Form 941 for 2026"
          description="Find the correct IRS mailing address for filing Form 941 based on your state and payment status. Ensure accurate filing and timely delivery."
          keywords=""
          canonical="resources/employment-tax/form-941-mailing-address/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <ExitPopup941 />
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 2,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941</span>
          </span>{' '}
          Mailing Address
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 5,
          }}
        >
          Understand the 941 mailing instructions to ensure your form is sent to
          the correct IRS address.
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-mailing-address/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 Mailing Address at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 941</b>
                        </Link>
                        {' is filed quarterly '}
                        {'with the IRS '}
                        {
                          "by employers to report federal income tax withheld from employees' wages and the employer and employee shares of Social Security and Medicare taxes. If you choose to "
                        }
                        <b>paper file Form 941</b>
                        {', the correct IRS mailing address depends on your '}
                        <b>
                          business location and whether you are including a
                          payment
                        </b>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Mailing Address:</b>
                        {
                          ' Employers should use the IRS mailing address assigned to their state and filing situation. Returns with payments are generally mailed to the IRS address in '
                        }
                        <b>Louisville, KY</b>
                        {
                          ', while returns without payments are generally sent to '
                        }
                        <b>Kansas City, MO, or Ogden, UT</b>, depending on the
                        employer's location.
                      </span>
                    </li>
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Due Dates</b>
                        </Link>
                        <b>:</b>
                        {' Form 941 is '}
                        generally due {'by '}
                        <b>{'April 30, July 31, October 31, and January '}</b>
                        <b>31</b>
                        <b>{'. '}</b>
                        If
                        {
                          ' a deadline falls on a weekend or legal holiday, the due date moves to the '
                        }
                        <b>next business day</b>.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 941
                      </Link>
                      , the Employer&apos;s Quarterly Federal Tax Return, is
                      used by U.S. employers to report income taxes, Social
                      Security tax, and Medicare tax withheld from
                      employees&apos; wages. This form is filed quarterly with
                      the IRS to help businesses remain current with their
                      payroll tax responsibilities.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Employers are required to file this form each quarter,
                      even if no employees are working during a particular
                      period, if the business is still operational. The form
                      reports wages paid, tax amounts withheld, and any
                      adjustments made throughout the quarter.{' '}
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '20px !important',
                      }}
                    >
                      E-Filing for Faster and More Secure Processing
                    </Typography>
                    <Typography variant="body2">
                      <span style={{ fontWeight: 600 }}>
                        Want quicker processing?
                      </span>{' '}
                      Switch to e-filing with TaxZerone and avoid delays. File{' '}
                      <span style={{ fontWeight: 600 }}>
                        Form 941 electronically
                      </span>{' '}
                      today - starting at{' '}
                      <span style={{ color: '#fff3b1', fontWeight: 600 }}>
                        just $6.99!
                      </span>
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#0bf473',
                        color: '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        '&:hover, &:focus, &:active': {
                          backgroundColor: '#fff',
                        },
                      }}
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941mail`}
                    >
                      E-file Form 941 Now
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: '#fff3b1',
                      py: 1,
                      px: 2,
                      border: '2px dashed #000',
                      color: '#000',
                      lineHeight: 1.3,
                    }}
                  >
                    <b>Grab 10% off</b> your filing fee — just enter{' '}
                    <b style={{ fontSize: 18, color: '#c02121' }}>TZOEMP10</b>{' '}
                    at checkout.
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to mail your Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 941
                  </Link>{' '}
                  by paper return; the mailing address depends on whether you
                  are{' '}
                  <span style={{ fontWeight: 600 }}>including a payment</span>{' '}
                  and the <span style={{ fontWeight: 600 }}>state</span> where
                  your business is located.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, the IRS recommends that you e-file Form 941 instead
                  of mailing — it’s faster, safer, and reduces processing
                  errors. With TaxZerone, you can{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 941
                  </Link>{' '}
                  from anywhere, without dealing with paperwork or long wait
                  times.
                </Typography>
                <Alert severity="info" icon={false} sx={{ mb: 4 }}>
                  <b>Important:</b> If you&apos;re using a Private Delivery
                  Service (PDS), remember they can&apos;t deliver to P.O. boxes.
                  For those cases, use the street addresses provided by the IRS.
                  You can find the approved list of PDSs at{' '}
                  <Link
                    href="https://www.irs.gov/pds"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS.gov/PDS
                  </Link>{' '}
                  and the correct street addresses at
                  <Link
                    href="https://www.irs.gov/pdsstreetaddresses"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS.gov/PDSstreetAddresses
                  </Link>
                  . Make sure to select the address based on your state — the
                  same one you&apos;d use when mailing a return without a
                  payment.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Mailing Addresses for Form 941
                </Typography>
                <Typography variant="body1">
                  Find the correct{' '}
                  <span style={{ fontWeight: 600 }}> 941 filing address</span>{' '}
                  below to ensure your 941 federal employer's quarterly tax
                  return is mailed to the IRS accurately.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            If you live in one of the following states:
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Mail the return without including payment
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Mail the return along with payment
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            Kansas City, MO 64999-0005
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0005
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            No legal residence or principal place of business in
                            any state, including employers in American Samoa,
                            Guam, the CNMI, the USVI, and Puerto Rico
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            PO Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Special filing address
                            </span>{' '}
                            for exempt organizations; federal, state, and local
                            governmental entities; and Indian tribal
                            governmental entities, regardless of location
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0005
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you pay by check or money order,{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Make it payable to{' '}
                    <span style={{ fontWeight: 600 }}>
                      &apos;United States Treasury.&apos;
                    </span>{' '}
                  </li>
                  <li>
                    Include your EIN,{' '}
                    <span style={{ fontWeight: 600 }}>Form 941</span>, and the
                    tax period (e.g., &apos;1st Quarter 2026,&apos; &apos;2nd
                    Quarter 2026,&apos; &apos;3rd Quarter 2026,&apos; or
                    &apos;4th Quarter 2026&apos;) on the check or money order.
                  </li>
                  <li>Complete Form 941-V and enclose it with Form 941.</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When Is Form 941 Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 941 must be filed quarterly. Here are the deadlines:
                </Typography>

                <Box
                  component="ul"
                  sx={{ mb: 2, '& li': { mb: 1.2 } }}
                  className="important-dates"
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      1st Quarter &nbsp; &nbsp;
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '30px 0 0 30px',
                          padding: '7px 13px',
                          backgroundColor: '#0896ba',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {' '}
                        Jan
                      </span>{' '}
                      –{' '}
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '0 30px 30px 0',
                          padding: '7px 15px',
                          backgroundColor: '#0896ba',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Mar
                      </span>
                      &nbsp; :
                    </span>{' '}
                    Due by April 30{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      2nd Quarter &nbsp;
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '30px 0 0 30px',
                          padding: '7px 15px',
                          backgroundColor: '#3dbc9e',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Apr
                      </span>{' '}
                      –{' '}
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '0 30px 30px 0',
                          padding: '7px 15px',
                          backgroundColor: '#3dbc9e',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Jun
                      </span>
                      &nbsp; :
                    </span>{' '}
                    Due by July 31{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      3rd Quarter &nbsp; &nbsp;
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '30px 0 0 30px',
                          padding: '7px 15px',
                          backgroundColor: '#a9bf43',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Jul
                      </span>{' '}
                      –{' '}
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '0 30px 30px 0',
                          padding: '7px 15px',
                          backgroundColor: '#a9bf43',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Sep{' '}
                      </span>{' '}
                      &nbsp; :{' '}
                    </span>{' '}
                    Due by October 31{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      4th Quarter &nbsp; &nbsp;
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '30px 0 0 30px',
                          padding: '7px 13px',
                          backgroundColor: '#cf9354',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Oct
                      </span>{' '}
                      –{' '}
                      <span
                        style={{
                          fontWeight: 600,
                          borderRadius: '0 30px 30px 0',
                          padding: '7px 15px',
                          backgroundColor: '#cf9354',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        Dec
                      </span>{' '}
                      &nbsp;:{' '}
                    </span>{' '}
                    Due by January 31
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It&apos;s important to meet these{' '}
                  <Link
                    href="/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    941 deadlines
                  </Link>{' '}
                  to avoid penalties and interest charges from the IRS.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, fontSize: '13.5px' }}
                >
                  <b>Note:</b> If any deadline falls on a weekend or legal
                  holiday, the due date is extended to the next business day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why E-Filing is Better Than Paper Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  E-filing offers several advantages over paper filing, making
                  it a more efficient option for employers.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-mailing-img.webp" />
                  <source srcSet="/assets/images/941-mailing-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-mailing-img.png"
                    loading="lazy"
                    alt="  Why E-Filing is Better Than Paper Filing"
                    width="100%"
                    height="100%"
                    className="img-responsive"
                  />
                </picture>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 4,
                  }}
                >
                  E-File Your Form 941 with Ease Using TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-file your{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 941
                  </Link>{' '}
                  through TaxZerone—quick, secure, and affordable for businesses
                  filing single or multiple forms.{' '}
                  <span style={{ fontWeight: 600 }}>
                    Need to make corrections?
                  </span>{' '}
                  TaxZerone also supports{' '}
                  <Link
                    href="/resources/employment-tax/form-941-x-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941-X
                  </Link>
                  , helping you fix previously filed returns with ease. Starting
                  at{' '}
                  <span style={{ fontWeight: 600, color: '#213e99' }}>
                    just $6.99
                  </span>
                  , our IRS-authorized platform is built for accuracy and
                  efficiency, making your quarterly federal tax filing process
                  smooth and reliable every time.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on the guidelines from
                        the IRS website.
                        <br style={{ marginBottom: 8 }} />
                        For further details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions
                        </Link>{' '}
                        for Form 941 and the{' '}
                        <Link
                          href="https://www.irs.gov/filing/where-to-file-your-taxes-for-form-941"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          mailing addresses for Form 941 submissions
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Simplify Your Quarterly Tax Filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Join thousands of businesses using TaxZerone for{' '}
              <Link
                href="/employment-tax/e-file-irs-form-941/"
                sx={{ color: '#0202ff' }}
              >
                Form 941 e-filing
              </Link>
              . Create your account today
              <br /> and experience the easiest way to manage your employment
              tax obligations.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941mail`}
              >
                Create a Free Account Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941MailAddress;
